<template>
    <div class="profile-container">

        <!-- Main Content -->
        <div class="main-content">
            <!-- Loading State -->
            <LoadingSpinner v-if="loading" />

            <!-- Profile Content -->
            <div v-else class="profile-section">
                <!-- Profile Header -->
                <ProfileHeader
                    :user-name="user?.full_name || 'Student'"
                    :subscription-tier="subscriptionTier"
                />

                <!-- Profile Information Cards -->
                <div class="profile-cards">
                    <!-- Personal Information Card -->
                    <PersonalInfoCard
                        :user="user"
                        :saving="savingPersonal"
                        @save="savePersonalInfo"
                        @cancel="onPersonalCancel"
                    />

                    <!-- Security Card -->
                    <SecurityCard
                        :saving="savingPassword"
                        @change-password="changePassword"
                        @cancel="onPasswordCancel"
                    />

                    <!-- Subscription Card -->
                    <SubscriptionCard
                        :subscription-tier="subscriptionTier"
                        :subscription-expires-at="subscriptionExpiresAt"
                        :upgrading="upgradingTier"
                        :get-tier-level="getTierLevel"
                        :has-active-subscription="hasActiveSubscription"
                        :is-canceled-at-period-end="isCanceledAtPeriodEnd"
                        :managing-subscription="managingSubscription"
                        @upgrade-tier="upgradeTier"
                        @manage-subscription="openManageSubscription"
                    />
                </div>
            </div>
        </div>

        <!-- Toast Notification -->
        <ToastNotification
            :show="showToast"
            :message="toastMessage"
            :type="toastType"
        />

        <!-- Guest Blocking Modal -->
        <GuestBlockingModal
            :show="isGuest"
            featureName="Profile"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import api from '@/services/api';

import LoadingSpinner from '@/components/practice/LoadingSpinner.vue';
import ProfileHeader from '@/components/profile/ProfileHeader.vue';
import PersonalInfoCard from '@/components/profile/PersonalInfoCard.vue';
import SecurityCard from '@/components/profile/SecurityCard.vue';
import SubscriptionCard from '@/components/profile/SubscriptionCard.vue';
import ToastNotification from '@/components/profile/ToastNotification.vue';
import GuestBlockingModal from '@/components/common/GuestBlockingModal.vue';

export default {
    name: 'Profile',
    components: {
        LoadingSpinner,
        ProfileHeader,
        PersonalInfoCard,
        SecurityCard,
        SubscriptionCard,
        ToastNotification,
        GuestBlockingModal
    },
    setup() {
        // ==================== SETUP ====================
        const router = useRouter();
        const route = useRoute();
        const authStore = useAuthStore();

        // ==================== STATE ====================
        const loading = ref(true);
        const savingPersonal = ref(false);
        const savingPassword = ref(false);
        const upgradingTier = ref(false);
        const managingSubscription = ref(false);

        // Toast notification
        const showToast = ref(false);
        const toastMessage = ref('');
        const toastType = ref('success');

        // ==================== COMPUTED ====================
        const user = computed(() => authStore.user);
        const isGuest = computed(() => !authStore.isAuthenticated);

        const subscriptionTier = computed(() => {
            return user.value?.subscription_tier || 'FREE';
        });

        const subscriptionExpiresAt = computed(() => {
            return user.value?.premium_expires_at || null;
        });

        // Check if user has an active Stripe subscription
        const hasActiveSubscription = computed(() => {
            return !!user.value?.has_active_subscription;
        });

        // Check if subscription is canceled but still active until period end
        const isCanceledAtPeriodEnd = computed(() => {
            return !!user.value?.subscription_cancel_at_period_end;
        });

        // ==================== METHODS ====================
        const showToastNotification = (message, type = 'success') => {
            toastMessage.value = message;
            toastType.value = type;
            showToast.value = true;
            setTimeout(() => {
                showToast.value = false;
            }, 3000);
        };

        const loadUserProfile = async () => {
            loading.value = true;
            try {
                await authStore.fetchUser();
            } catch (error) {
                console.error('Error loading profile:', error);
                showToastNotification('Failed to load profile', 'error');
            } finally {
                loading.value = false;
            }
        };

        const savePersonalInfo = async (formData) => {
            savingPersonal.value = true;
            try {
                await api.updateProfile({
                    full_name: formData.full_name,
                    email: formData.email,
                    school: formData.school
                });

                await authStore.fetchUser();
                showToastNotification('Profile updated successfully');
            } catch (error) {
                console.error('Error updating profile:', error);
                showToastNotification('Failed to update profile', 'error');
            } finally {
                savingPersonal.value = false;
            }
        };

        const onPersonalCancel = () => {
            // Handle cancel if needed
        };

        const changePassword = async (passwordData) => {
            savingPassword.value = true;
            try {
                await api.changePassword({
                    current_password: passwordData.currentPassword,
                    new_password: passwordData.newPassword
                });

                showToastNotification('Password updated successfully');
            } catch (error) {
                console.error('Error changing password:', error);
                if (error.response?.status === 429) {
                    showToastNotification('Too many attempts. Please slow down and try again later.', 'error');
                    return;
                }
                const detail = error.response?.data?.detail;
                let message = 'Failed to update password';
                if (typeof detail === 'string') {
                    message = detail;
                } else if (Array.isArray(detail) && detail.length > 0) {
                    message = detail[0].msg || message;
                }
                showToastNotification(message, 'error');
            } finally {
                savingPassword.value = false;
            }
        };

        const onPasswordCancel = () => {
            // Handle cancel if needed
        };

        const getTierLevel = (tier) => {
            return authStore.getTierLevel(tier);
        };

        // Initiate Stripe Checkout for paid tier upgrade
        const upgradeTier = async ({ tier, billingCycle }) => {
            upgradingTier.value = true;
            try {
                await authStore.initiateCheckout(tier, billingCycle);
                // User will be redirected to Stripe Checkout
            } catch (error) {
                console.error('Checkout error:', error);
                showToastNotification(error.message || 'Failed to start checkout', 'error');
                upgradingTier.value = false;
            }
        };

        // Open Stripe Customer Portal for subscription management
        const openManageSubscription = async () => {
            managingSubscription.value = true;
            try {
                await authStore.openCustomerPortal();
                // User will be redirected to Stripe Customer Portal
            } catch (error) {
                console.error('Portal error:', error);
                showToastNotification(error.message || 'Failed to open billing portal', 'error');
            } finally {
                managingSubscription.value = false;
            }
        };

        const handleNavigation = (tabId) => {
            router.push(`/${tabId}`);
        };

        const handleLogout = () => {
            authStore.logout();
            router.push('/');
        };

        // ==================== LIFECYCLE ====================
        onMounted(async () => {
            // Only load profile for authenticated users
            if (authStore.isAuthenticated) {
                await loadUserProfile();

                // Handle payment success/cancel redirect from Stripe
                const paymentStatus = route.query.payment;
                if (paymentStatus === 'success') {
                    showToastNotification('Payment successful! Your subscription is now active.', 'success');
                    // Refresh user data to get updated subscription
                    await authStore.fetchUser();
                    // Clean up URL query params
                    router.replace({ path: '/profile' });
                } else if (paymentStatus === 'canceled') {
                    showToastNotification('Payment was canceled.', 'info');
                    router.replace({ path: '/profile' });
                }
            } else {
                loading.value = false;
            }
        });

        // ==================== RETURN ====================
        return {
            loading,
            user,
            isGuest,
            subscriptionTier,
            subscriptionExpiresAt,
            hasActiveSubscription,
            isCanceledAtPeriodEnd,
            savingPersonal,
            savingPassword,
            upgradingTier,
            managingSubscription,
            showToast,
            toastMessage,
            toastType,
            savePersonalInfo,
            onPersonalCancel,
            changePassword,
            onPasswordCancel,
            upgradeTier,
            openManageSubscription,
            getTierLevel,
            handleNavigation,
            handleLogout
        };
    }
};
</script>

<style scoped>
* {
    font-family: "Space Grotesk";
    font-weight: 400;
    letter-spacing: 1px;
}

::-webkit-scrollbar {
    width: 20px;
}

::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 10px;
}

.profile-container {
    min-height: 100vh;
    background: var(--bg-primary);
    justify-content: center;
    display: flex;
}

.main-content {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
}

@media screen and (max-width: 1400px) {
    .main-content {
        padding: 0;
        margin: 0;
    }
}

@media screen and (max-width: 1200px) {
    .main-content {
        max-width: 100%;
        padding: 0;
        margin: 0;
    }
    .profile-section {
        padding: 70px 15%;
    }
}

@media screen and (max-width: 768px) {
    .main-content {
        padding: 0;
        margin: auto;
    }
}

.profile-section {
    background: var(--bg-primary);
    padding: 70px 10%;
}

/* Profile Cards */
.profile-cards {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .profile-section {
        padding: 20px;
    }
}
</style>
